import React, { useState, useEffect } from 'react';
import { Modal } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import useMembershipAuthHelpers from '../../hooks/useMembershipAuthHelpers';
import Layout, { Props as LayoutProps } from './Layout';
import MembershipLoginForm from '../../modules/Auth/Membership/MembershipLoginForm';
import MembershipRegistrationForm from '../../modules/Auth/Membership/MembershipRegistrationForm';
import OTPForm from '../../modules/Auth/OtpForm/OtpForm';
import './LayoutWithMembershipGuard.less';
import { useLocation, useNavigate } from 'react-router-dom';
import { OtpMode, setIsOtpAuthCheckModalOpen } from '../../store/config';
import getImagePathFromName from '@skygroup/shared/utils/getImagePathFromName';

export enum AuthView {
  LOGIN = 'login',
  REGISTRATION = 'registration',
  OTP = 'otp',
}

interface LayoutWithMembershipGuardProps extends LayoutProps {}

const LayoutWithMembershipGuard: React.FC<LayoutWithMembershipGuardProps> = (
  props
) => {
  // const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoginMembershipModalOpen, setIsLoginMembershipModalOpen] =
    useState(false);
  const isIndexRoute = location.pathname === '/';
  const { isAuthenticated } = useMembershipAuthHelpers();

  const {
    isOtpAuthCheckModalOpen,
    membershipUser,
    translations: t,
    config,
    agentInfo
  } = useSelector((data: RootState) => data.config);

  const [isLoginSuccess, setIsLoginSuccess] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [authView, setAuthView] = useState<AuthView | null>(null);
  const isMembershipFreePath = location.pathname !== '/order/manage' && location.pathname !== '/takanon' && !agentInfo?.agentMode;
  const requireMembership =
    config?.siteConfig?.membership?.requireMembership === 1;
  const isUserAuthenticated = isAuthenticated || !!membershipUser;
  const shouldShowMask = requireMembership && !isUserAuthenticated && isMembershipFreePath;

  useEffect(() => {
    if (!requireMembership) {
      return;
    }

    // if not index route and user is not authenticated, redirect to home and open login modal
    if (!isIndexRoute && !isUserAuthenticated && isMembershipFreePath) {
      navigate('/');
      setIsLoginMembershipModalOpen(true);
      return;
    }

    // If user becomes authenticated, close the modal and hide the mask
    if (isUserAuthenticated) {
      setIsLoginMembershipModalOpen(false);
      setIsLoginSuccess(false);
      setSessionId(null);
      setAuthView(AuthView.LOGIN);
    }
  }, [requireMembership, isUserAuthenticated, isIndexRoute, navigate]);

  const handleMaskClick = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsLoginMembershipModalOpen(true);
  };

  const handleModalCancel = () => {
    setIsLoginMembershipModalOpen(false);
    setIsLoginSuccess(false);
    setSessionId(null);
    setAuthView(AuthView.LOGIN);
  };

  const handleRegisterClick = () => {
    setAuthView(AuthView.REGISTRATION);
  };

  const handleBackToLogin = (loginSessionId?: string, autoLogin?: boolean) => {
    if (autoLogin && loginSessionId) {
      // Auto-login flow: set session and go to OTP
      setSessionId(loginSessionId);
      setIsLoginSuccess(true);
      setAuthView(AuthView.OTP);
    } else {
      // Normal flow: reset and go to login
      setAuthView(AuthView.LOGIN);
      setIsLoginSuccess(false);
      setSessionId(null);
    }
  };

  const otpMode = isOtpAuthCheckModalOpen
    ? OtpMode.ORG_ISTADRUT
    : isLoginMembershipModalOpen
    ? OtpMode.MEMBERSHIP
    : null;

  const getModalTitle = () => {
    if (authView === AuthView.REGISTRATION) {
      return null;
    }

    if (otpMode === OtpMode.ORG_ISTADRUT) {
      return (
        <div className={`membership-login-header ${otpMode === OtpMode.ORG_ISTADRUT ? 'org-istadrut' : ''}`}>
          {config?.siteConfig?.membership?.title && <h2>{config?.siteConfig?.membership?.title}</h2>}
          {config?.siteConfig?.membership?.subtitle ? config?.siteConfig?.membership?.subtitle : t?.auth.authTitle}
        </div>
      );
    }

    if (otpMode === OtpMode.MEMBERSHIP && authView === AuthView.LOGIN) {
      return (
        <div className={`membership-login-header ${authView === AuthView.LOGIN ? 'membership login' : ''}`}>
          {config?.siteConfig?.membership?.title && <h2>{config?.siteConfig?.membership?.title}</h2>}
          {config?.siteConfig?.membership?.subtitle ? config?.siteConfig?.membership?.subtitle : t?.auth.loginTitle}
        </div>
      );
    }

    return (
      <div className={`membership-login-header membership`}>
        {config?.siteConfig?.membership?.title && <h2>{config?.siteConfig?.membership?.title}</h2>}
        {config?.siteConfig?.membership?.subtitle ? config?.siteConfig?.membership?.subtitle : t?.auth.authWithMobile}
      </div>
    );
  };
const renderLogo = () => {
    return (
        <img className={"membership-logo"} src={getImagePathFromName(config?.siteConfig.logo)} alt="logo" />
    );
  };
  return (
    <>
      {/*
        On index ('/') we still render the page and just put a mask over it.
        On any other route, when membership is required and user is not authenticated,
        we do NOT render Layout at all (so page components are not executed).
      */}
      {(!shouldShowMask || isIndexRoute) && <Layout {...props} />}

      {shouldShowMask && (
        <div
          className={`membership-mask-overlay ${
            isLoginMembershipModalOpen ? 'is-active' : ''
          }`}
          onClick={handleMaskClick}
          onTouchStart={handleMaskClick}
        >
          {!isIndexRoute && (
            <img
              src={config?.siteConfig?.membership?.backgroundImage}
              alt="city-blurred"
            />
          )}
        </div>
      )}
      {/* auth modal */}
      {otpMode && (
        <Modal
          className={`upgrade-plan-template-modal membership-modal ${
            authView === AuthView.REGISTRATION
              ? 'registration-mode'
              : 'login-mode'
          }`}
          open={!!otpMode}
          title={
            <div className={"membership-logo-header"} >
              {renderLogo()}
              {getModalTitle()}
            </div>
          }          onCancel={handleModalCancel}
          footer={null}
          style={{ background: '#F5F5F5' }}
          centered
          closable={false}
          maskClosable={false}
        >
          {authView === AuthView.REGISTRATION ? (
            <MembershipRegistrationForm
              isUpdateProfileMode={false}
              onBackToLogin={handleBackToLogin}
              onLoginClick={handleBackToLogin}
            />
          ) : !isLoginSuccess ? (
            <MembershipLoginForm
              otpMode={otpMode}
              setIsLoginSuccess={setIsLoginSuccess}
              setSessionId={setSessionId}
              onRegisterClick={
                otpMode === OtpMode.MEMBERSHIP ? handleRegisterClick : undefined
              }
            />
          ) : (
            <OTPForm
              otpMode={otpMode}
              setIsLoginSuccess={setIsLoginSuccess}
              sessionId={sessionId!}
            />
          )}
        </Modal>
      )}
    </>
  );
};

export default LayoutWithMembershipGuard;